import { createGlobalStyle } from 'styled-components';

import { getConfig } from '@nubank/www-latam-commons/config';

const config = getConfig();

const ATTEMPT_TIMEOUT_MS = 10000;
const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 400;
const POLL_INTERVAL_MS = 100;

export const RECAPTCHA_TOKEN_NOT_AVAILABLE = 'recaptcha-token-not-available';

export const RECAPTCHA_FAILURE_REASON = {
  SCRIPT_UNAVAILABLE: 'script-unavailable',
  TIMEOUT: 'timeout',
  EXECUTE_FAILED: 'execute-failed',
};

const RETRYABLE_REASONS = [
  RECAPTCHA_FAILURE_REASON.EXECUTE_FAILED,
];

export class RecaptchaTokenError extends Error {
  constructor({ reason, attempts, cause }) {
    super(RECAPTCHA_TOKEN_NOT_AVAILABLE);
    this.name = 'RecaptchaTokenError';
    // Single identifier shared by Sentry and analytics, so a captcha failure is
    // greppable across both regardless of where it surfaced.
    this.code = RECAPTCHA_TOKEN_NOT_AVAILABLE;
    this.reason = reason;
    this.attempts = attempts;
    this.cause = cause;
  }
}

export const isRecaptchaTokenError = error => error?.code === RECAPTCHA_TOKEN_NOT_AVAILABLE;

const delay = ms => new Promise(resolve => { setTimeout(resolve, ms); });

// Resolves with `grecaptcha.enterprise` as soon as the async script lands.
const waitForEnterprise = (deadline, pollIntervalMs) => new Promise((resolve, reject) => {
  const poll = () => {
    const enterprise = window.grecaptcha?.enterprise;

    if (enterprise) {
      resolve(enterprise);
      return;
    }

    if (Date.now() >= deadline) {
      reject(new RecaptchaTokenError({
        reason: RECAPTCHA_FAILURE_REASON.SCRIPT_UNAVAILABLE,
        cause: new Error('reCAPTCHA script never became available'),
      }));
      return;
    }

    setTimeout(poll, pollIntervalMs);
  };

  poll();
});

const executeWithDeadline = (enterprise, deadline) => new Promise((resolve, reject) => {
  let settled = false;

  const timeoutId = setTimeout(() => {
    settled = true;
    reject(new RecaptchaTokenError({
      reason: RECAPTCHA_FAILURE_REASON.TIMEOUT,
      cause: new Error('reCAPTCHA execution timed out'),
    }));
  }, Math.max(0, deadline - Date.now()));

  // Guarded so a token arriving after the deadline can't settle an already
  // rejected attempt.
  const settle = (fn, value) => {
    if (settled) {
      return;
    }
    settled = true;
    clearTimeout(timeoutId);
    fn(value);
  };

  const fail = error => settle(reject, new RecaptchaTokenError({
    reason: RECAPTCHA_FAILURE_REASON.EXECUTE_FAILED,
    cause: error,
  }));

  try {
    enterprise.ready(() => {
      if (settled) {
        return;
      }
      try {
        enterprise
          .execute(config.recaptchaSiteKey, { action: 'submit' })
          .then(token => settle(resolve, token))
          .catch(fail);
      } catch (error) {
        fail(error);
      }
    });
  } catch (error) {
    fail(error);
  }
});

const attemptToken = async ({
  attempt, attemptTimeoutMs, maxAttempts, retryDelayMs, pollIntervalMs,
}) => {
  try {
    // One budget covers waiting for the script plus executing, so a slow script
    // load eats into the same ceiling rather than extending it.
    const deadline = Date.now() + attemptTimeoutMs;
    const enterprise = await waitForEnterprise(deadline, pollIntervalMs);

    return await executeWithDeadline(enterprise, deadline);
  } catch (error) {
    error.attempts = attempt;

    if (!RETRYABLE_REASONS.includes(error.reason) || attempt >= maxAttempts) {
      throw error;
    }

    await delay(retryDelayMs);

    return attemptToken({
      attempt: attempt + 1, attemptTimeoutMs, maxAttempts, retryDelayMs, pollIntervalMs,
    });
  }
};

/*
Options exist so tests can run with millisecond timings; no call site passes
them. Rejects with a `RecaptchaTokenError` carrying `reason`, `attempts` and the
underlying `cause`.
*/
export const get = ({
  attemptTimeoutMs = ATTEMPT_TIMEOUT_MS,
  maxAttempts = MAX_ATTEMPTS,
  retryDelayMs = RETRY_DELAY_MS,
  pollIntervalMs = POLL_INTERVAL_MS,
} = {}) => attemptToken({
  attempt: 1, attemptTimeoutMs, maxAttempts, retryDelayMs, pollIntervalMs,
});

export const buildRecaptchaHeaders = captchaTokens => {
  if (!captchaTokens?.googleRecaptcha) {
    return undefined;
  }
  return {
    // 'x-recaptcha-token-type': 'enterprise' or 'v3'
    'x-recaptcha-token-type': 'enterprise',
    'x-recaptcha-token': captchaTokens.googleRecaptcha,
  };
};

export const getCaptchaTokens = async options => ({
  googleRecaptcha: await get(options),
});

const WARM_TIMEOUT_MS = 30000;
const WARM_POLL_INTERVAL_MS = 250;

/*
Load and prepare reCAPTCHA when the screen opens, without requesting a token yet.
Failures are ignored so the form is not blocked.
*/
export const warmRecaptcha = ({
  timeoutMs = WARM_TIMEOUT_MS,
  pollIntervalMs = WARM_POLL_INTERVAL_MS,
} = {}) => {
  const deadline = Date.now() + timeoutMs;

  return waitForEnterprise(deadline, pollIntervalMs)
    .then(enterprise => new Promise(resolve => {
      try {
        enterprise.ready(resolve);
      } catch {
        resolve();
      }
    }))
    .catch(() => undefined);
};

export const RecaptchaGlobalStyle = createGlobalStyle`
  .grecaptcha-badge {
    width: auto !important;
    position: sticky !important;
    margin-top: -150px;
    margin-left: calc(100vw - 70px);
    transition: margin-left 0.3s ease-in-out !important;

    &:hover {
      margin-left: calc(100vw - 256px);
    }

    @media (width >= 768px) {
      margin-top: -60px;
    }
  }
`;
